import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { AUTH_CONFIG } from './auth.config';
import { AuthService } from './auth.service';
import { RAW_HTTP_ERROR } from './error.interceptor';

/** `EmployeeV3Response`, identity `api-specs.md` §10.3. */
export interface EmployeeMe {
  id: string;
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  title: string;
  /** what an administrator GRANTED: the label on My account and the Employees table, and what the roles editor edits */
  roles: string[];
  /**
   * `roles` expanded down identity's ladder (own roles plus every role of strictly lower rank),
   * exactly as the token carries them: what the account can actually OPEN. Gate on this, never
   * on `roles`; never post it back (`task-adopt-effective-roles.md`). Absent from an identity
   * behind 2026-09-07.
   */
  effectiveRoles?: string[];
  active: boolean;
  /** false = no password has ever been set */
  credentialed: boolean;
  createdAt: string;
  updatedAt: string;
}

/**
 * The signed-in employee's own login (docs/ui/my-account.md). Two calls, no id in either: `me`
 * comes from the token, so this can never reach another employee.
 *
 * `enabled` is false until the app configures `mePath`; the screen and its entry point follow it.
 */
@Injectable({ providedIn: 'root' })
export class MeService {
  private readonly config = inject(AUTH_CONFIG);
  private readonly http = inject(HttpClient);
  private readonly auth = inject(AuthService);
  private readonly router = inject(Router);

  readonly enabled = !!this.config.mePath;

  private url(): string {
    return this.config.baseUrl + (this.config.mePath ?? '');
  }

  /** Through the interceptor on purpose: an expired token on this read still refreshes. */
  me(): Observable<EmployeeMe> {
    return this.http.get<EmployeeMe>(this.url());
  }

  /**
   * Raw errors on purpose (decision 3): the interceptor treats every 403 as token trouble and
   * fires a refresh, and flattens the body. `CURRENT_PASSWORD_INCORRECT` is a 403 the screen
   * needs to read; a 401 here means the account was deactivated, and the screen signs out.
   */
  changePassword(currentPassword: string, newPassword: string): Observable<EmployeeMe> {
    return this.http.post<EmployeeMe>(
      `${this.url()}:change-password`,
      { currentPassword, newPassword },
      { context: new HttpContext().set(RAW_HTTP_ERROR, true) },
    );
  }

  /** The account is closed and a live token cannot reopen it. Same exit as the interceptor's forced logout. */
  signOutDeactivated(): void {
    this.auth.logOut();
    this.router.navigate([this.config.loginPath]).then(() => {
      if (this.config.reloadOnLogout) window.location.reload();
    });
  }
}

/** identity v3 error envelope (§3.3): `{ error: { code, message, target?, requestId } }`. Empty on a filter 401/403. */
export function v3Error(err: unknown): { code?: string; message?: string; target?: string } {
  const body = (err as { error?: unknown } | null)?.error;
  const e = (body as { error?: { code?: string; message?: string; target?: string } } | null)?.error;
  return e ?? {};
}
